// src/context/EditorContext.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var defaultConfig = {
  getAccessToken: async () => null,
  apiBaseUrl: "http://localhost:3000",
  portalType: "user-tenant",
  features: {
    aiGeneration: true,
    pdfUpload: true,
    scanDocument: false,
    templateLibrary: true
  },
  callbacks: {}
};
var EditorContext = createContext(defaultConfig);
function EditorContextProvider({ config, children }) {
  const mergedConfig = {
    ...defaultConfig,
    ...config,
    features: {
      ...defaultConfig.features,
      ...config.features
    },
    callbacks: {
      ...defaultConfig.callbacks,
      ...config.callbacks
    }
  };
  return /* @__PURE__ */ jsx(EditorContext.Provider, { value: mergedConfig, children });
}
function useEditorConfig() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error("useEditorConfig must be used within an EditorContextProvider");
  }
  return context;
}
function useAuthenticatedFetch() {
  const { getAccessToken, apiBaseUrl } = useEditorConfig();
  return async function authenticatedFetch(endpoint, options = {}) {
    const token = await getAccessToken();
    const headers = new Headers(options.headers);
    headers.set("Content-Type", "application/json");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const url = endpoint.startsWith("http") ? endpoint : `${apiBaseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `Request failed: ${response.status}`);
    }
    const json = await response.json();
    if (json && typeof json === "object" && "success" in json && "data" in json) {
      if (!json.success) {
        throw new Error(json.error?.message || "Request failed");
      }
      return json.data;
    }
    return json;
  };
}
function useFeatureEnabled(feature) {
  const { features } = useEditorConfig();
  return features[feature];
}
function useIsSuperAdmin() {
  const { portalType } = useEditorConfig();
  return portalType === "super-admin";
}
function useErrorHandler() {
  const { callbacks } = useEditorConfig();
  return (error) => {
    console.error("[TemplateEditor]", error);
    callbacks.onError?.(error);
  };
}

// src/components/AIDocumentWizard.tsx
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Sparkles,
  Wand2,
  Loader2,
  AlertCircle,
  RefreshCw,
  FileText,
  Type,
  Heading1,
  List,
  Image,
  Table2,
  Minus,
  MoveVertical,
  LayoutGrid,
  Scissors,
  PenTool,
  Calendar,
  Clock,
  ChevronDown,
  Upload,
  PenLine,
  Check,
  FileUp,
  FileType2,
  X,
  Search,
  BookOpen
} from "lucide-react";
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var API_ENDPOINTS = {
  GENERATE_NDF: "/api/ai/generate-ndf",
  IMPORT_DOCUMENT: "/api/ai/import-document",
  PROMPT_TEMPLATES: "/api/ai/prompt-templates",
  PROMPT_TEMPLATE_USE: (id) => `/api/ai/prompt-templates/${id}/use`
};
var IMPORT_SUPPORTED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "gif", "webp"];
var IMPORT_MAX_SIZE = 20 * 1024 * 1024;
var categoryColorMap = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", iconBg: "bg-blue-100", border: "border-blue-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", iconBg: "bg-emerald-100", border: "border-emerald-200" },
  red: { bg: "bg-red-50", text: "text-red-700", iconBg: "bg-red-100", border: "border-red-200" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", iconBg: "bg-violet-100", border: "border-violet-200" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", iconBg: "bg-amber-100", border: "border-amber-200" },
  slate: { bg: "bg-slate-50", text: "text-slate-700", iconBg: "bg-slate-100", border: "border-slate-200" },
  yellow: { bg: "bg-yellow-50", text: "text-yellow-700", iconBg: "bg-yellow-100", border: "border-yellow-200" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", iconBg: "bg-indigo-100", border: "border-indigo-200" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", iconBg: "bg-teal-100", border: "border-teal-200" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", iconBg: "bg-rose-100", border: "border-rose-200" }
};
var kindBadgeConfig = {
  document: { gradient: "from-emerald-500 to-teal-500", label: "Document" },
  form: { gradient: "from-indigo-500 to-violet-500", label: "Form" },
  hybrid: { gradient: "from-amber-500 to-orange-500", label: "Hybrid" },
  sms: { gradient: "from-sky-500 to-cyan-500", label: "SMS" },
  email: { gradient: "from-rose-500 to-pink-500", label: "Email" }
};
var blockTypeConfig = {
  paragraph: { icon: /* @__PURE__ */ jsx2(Type, { className: "h-3.5 w-3.5" }), label: "Paragraph", color: "text-stone-600 dark:text-stone-400", bgColor: "bg-stone-100 dark:bg-stone-800" },
  heading: { icon: /* @__PURE__ */ jsx2(Heading1, { className: "h-3.5 w-3.5" }), label: "Heading", color: "text-indigo-600 dark:text-indigo-400", bgColor: "bg-indigo-50 dark:bg-indigo-950" },
  list: { icon: /* @__PURE__ */ jsx2(List, { className: "h-3.5 w-3.5" }), label: "List", color: "text-teal-600 dark:text-teal-400", bgColor: "bg-teal-50 dark:bg-teal-950" },
  image: { icon: /* @__PURE__ */ jsx2(Image, { className: "h-3.5 w-3.5" }), label: "Image", color: "text-pink-600 dark:text-pink-400", bgColor: "bg-pink-50 dark:bg-pink-950" },
  table: { icon: /* @__PURE__ */ jsx2(Table2, { className: "h-3.5 w-3.5" }), label: "Table", color: "text-cyan-600 dark:text-cyan-400", bgColor: "bg-cyan-50 dark:bg-cyan-950" },
  divider: { icon: /* @__PURE__ */ jsx2(Minus, { className: "h-3.5 w-3.5" }), label: "Divider", color: "text-stone-400 dark:text-stone-500", bgColor: "bg-stone-100 dark:bg-stone-800" },
  spacer: { icon: /* @__PURE__ */ jsx2(MoveVertical, { className: "h-3.5 w-3.5" }), label: "Spacer", color: "text-stone-400 dark:text-stone-500", bgColor: "bg-stone-100 dark:bg-stone-800" },
  section_header: { icon: /* @__PURE__ */ jsx2(LayoutGrid, { className: "h-3.5 w-3.5" }), label: "Section", color: "text-violet-600 dark:text-violet-400", bgColor: "bg-violet-50 dark:bg-violet-950" },
  page_break: { icon: /* @__PURE__ */ jsx2(Scissors, { className: "h-3.5 w-3.5" }), label: "Page Break", color: "text-stone-400 dark:text-stone-500", bgColor: "bg-stone-100 dark:bg-stone-800" },
  field_text: { icon: /* @__PURE__ */ jsx2(Type, { className: "h-3.5 w-3.5" }), label: "Text Field", color: "text-blue-600 dark:text-blue-400", bgColor: "bg-blue-50 dark:bg-blue-950" },
  field_date: { icon: /* @__PURE__ */ jsx2(Calendar, { className: "h-3.5 w-3.5" }), label: "Date Field", color: "text-amber-600 dark:text-amber-400", bgColor: "bg-amber-50 dark:bg-amber-950" },
  field_time: { icon: /* @__PURE__ */ jsx2(Clock, { className: "h-3.5 w-3.5" }), label: "Time Field", color: "text-orange-600 dark:text-orange-400", bgColor: "bg-orange-50 dark:bg-orange-950" },
  field_select: { icon: /* @__PURE__ */ jsx2(ChevronDown, { className: "h-3.5 w-3.5" }), label: "Select Field", color: "text-indigo-600 dark:text-indigo-400", bgColor: "bg-indigo-50 dark:bg-indigo-950" },
  field_file: { icon: /* @__PURE__ */ jsx2(Upload, { className: "h-3.5 w-3.5" }), label: "File Upload", color: "text-emerald-600 dark:text-emerald-400", bgColor: "bg-emerald-50 dark:bg-emerald-950" },
  field_signature: { icon: /* @__PURE__ */ jsx2(PenTool, { className: "h-3.5 w-3.5" }), label: "Signature", color: "text-rose-600 dark:text-rose-400", bgColor: "bg-rose-50 dark:bg-rose-950" },
  pdf_overlay: { icon: /* @__PURE__ */ jsx2(FileText, { className: "h-3.5 w-3.5" }), label: "PDF Overlay", color: "text-stone-500 dark:text-stone-400", bgColor: "bg-stone-100 dark:bg-stone-800" }
};
var defaultBlockConfig = { icon: /* @__PURE__ */ jsx2(Type, { className: "h-3.5 w-3.5" }), label: "Block", color: "text-stone-600", bgColor: "bg-stone-100" };
var containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.05 }
  },
  exit: { opacity: 0, transition: { duration: 0.15 } }
};
var itemVariants = {
  hidden: { opacity: 0, y: 14, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } }
};
var slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 30 }
  },
  exit: (direction) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
    transition: { duration: 0.2 }
  })
};
var buttonVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.02, transition: { type: "spring", stiffness: 400, damping: 25 } },
  tap: { scale: 0.98, transition: { type: "spring", stiffness: 400, damping: 25 } }
};
function BlockPreview({ block, index }) {
  const config = blockTypeConfig[block.type] || defaultBlockConfig;
  const renderContent = () => {
    switch (block.type) {
      case "heading": {
        const headingBlock = block;
        const levelSizes = { 1: "text-lg font-bold", 2: "text-base font-semibold", 3: "text-sm font-semibold", 4: "text-sm font-medium" };
        return /* @__PURE__ */ jsx2("p", { className: `${levelSizes[headingBlock.level || 2] || levelSizes[2]} text-stone-900 dark:text-stone-100 leading-snug`, children: headingBlock.content });
      }
      case "paragraph": {
        const paragraphBlock = block;
        return /* @__PURE__ */ jsx2("p", { className: "text-sm text-stone-700 dark:text-stone-300 leading-relaxed line-clamp-2", children: paragraphBlock.content });
      }
      case "list": {
        const listBlock = block;
        return /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          (listBlock.items || []).slice(0, 3).map((item, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 text-sm text-stone-700 dark:text-stone-300", children: [
            /* @__PURE__ */ jsx2("span", { className: "mt-0.5 shrink-0 text-stone-400 dark:text-stone-500", children: listBlock.ordered ? `${i + 1}.` : "\u2022" }),
            /* @__PURE__ */ jsx2("span", { className: "line-clamp-1", children: item })
          ] }, i)),
          (listBlock.items || []).length > 3 && /* @__PURE__ */ jsxs("p", { className: "text-xs text-stone-400 dark:text-stone-500 pl-5", children: [
            "+",
            (listBlock.items || []).length - 3,
            " more items"
          ] })
        ] });
      }
      case "image": {
        const imageBlock = block;
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400", children: [
          /* @__PURE__ */ jsx2("div", { className: "flex h-8 w-12 items-center justify-center rounded-md bg-stone-100 dark:bg-stone-700", children: /* @__PURE__ */ jsx2(Image, { className: "h-4 w-4 text-stone-400" }) }),
          /* @__PURE__ */ jsx2("span", { className: "italic", children: imageBlock.alt || "Image placeholder" })
        ] });
      }
      case "table": {
        const tableBlock = block;
        const headers = (tableBlock.headers || []).slice(0, 4);
        return /* @__PURE__ */ jsxs("div", { className: "overflow-hidden rounded border border-stone-200 dark:border-stone-700", children: [
          /* @__PURE__ */ jsx2("div", { className: "grid gap-px bg-stone-200 dark:bg-stone-700", style: { gridTemplateColumns: `repeat(${Math.max(headers.length, 1)}, 1fr)` }, children: headers.map((h, i) => /* @__PURE__ */ jsx2("div", { className: "bg-stone-50 px-2 py-1 text-xs font-medium text-stone-600 dark:bg-stone-800 dark:text-stone-400 truncate", children: h }, i)) }),
          (tableBlock.rows || []).length > 0 && /* @__PURE__ */ jsxs("p", { className: "px-2 py-1 text-xs text-stone-400 dark:text-stone-500", children: [
            (tableBlock.rows || []).length,
            " row",
            (tableBlock.rows || []).length !== 1 ? "s" : ""
          ] })
        ] });
      }
      case "section_header": {
        const sectionBlock = block;
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx2("p", { className: "font-semibold text-stone-800 dark:text-stone-200", children: sectionBlock.title }),
          sectionBlock.subtitle && /* @__PURE__ */ jsx2("p", { className: "text-xs text-stone-500 dark:text-stone-400", children: sectionBlock.subtitle })
        ] });
      }
      case "field_text":
      case "field_date":
      case "field_time":
      case "field_select":
      case "field_file":
      case "field_signature": {
        const fieldBlock = block;
        return /* @__PURE__ */ jsx2("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsxs("p", { className: "text-sm font-medium text-stone-700 dark:text-stone-300", children: [
            fieldBlock.label,
            fieldBlock.required && /* @__PURE__ */ jsx2("span", { className: "ml-1 text-red-500", children: "*" })
          ] }),
          fieldBlock.placeholder && /* @__PURE__ */ jsx2("p", { className: "text-xs text-stone-400 dark:text-stone-500 italic", children: fieldBlock.placeholder })
        ] }) });
      }
      case "divider":
        return /* @__PURE__ */ jsx2("div", { className: "my-1 h-px w-full bg-stone-200 dark:bg-stone-700" });
      case "spacer": {
        const spacerBlock = block;
        return /* @__PURE__ */ jsxs("p", { className: "text-xs text-stone-400 dark:text-stone-500 italic", children: [
          "Spacer (",
          spacerBlock.height || 16,
          "px)"
        ] });
      }
      case "page_break":
        return /* @__PURE__ */ jsxs("p", { className: "text-xs text-stone-400 dark:text-stone-500 italic flex items-center gap-1", children: [
          /* @__PURE__ */ jsx2(Scissors, { className: "h-3 w-3" }),
          "Page break"
        ] });
      default:
        return /* @__PURE__ */ jsx2("p", { className: "text-sm text-stone-500 dark:text-stone-400 italic", children: "Block content" });
    }
  };
  return /* @__PURE__ */ jsxs(
    motion.div,
    {
      initial: { opacity: 0, y: 8 },
      animate: { opacity: 1, y: 0 },
      transition: { delay: index * 0.03, type: "spring", stiffness: 300, damping: 28 },
      className: "flex gap-3 rounded-lg border border-stone-200/80 bg-white p-3 dark:border-stone-700/50 dark:bg-stone-800/50",
      children: [
        /* @__PURE__ */ jsx2("div", { className: `mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${config.bgColor} ${config.color}`, children: config.icon }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx2("p", { className: `mb-1 text-[10px] font-semibold uppercase tracking-wider ${config.color}`, children: config.label }),
          renderContent()
        ] })
      ]
    }
  );
}
function TemplateSkeletons() {
  return /* @__PURE__ */ jsx2("div", { className: "grid grid-cols-2 gap-2 sm:grid-cols-3", children: Array.from({ length: 6 }).map((_, i) => /* @__PURE__ */ jsx2(
    "div",
    {
      className: "h-[76px] animate-pulse rounded-xl bg-stone-100 dark:bg-stone-800/60",
      style: { animationDelay: `${i * 60}ms` }
    },
    i
  )) });
}
function TemplateCard({ template, color, isSelected, onClick }) {
  const colors = categoryColorMap[color] || categoryColorMap.indigo;
  return /* @__PURE__ */ jsxs(
    motion.button,
    {
      variants: itemVariants,
      whileHover: { scale: 1.02 },
      whileTap: { scale: 0.97 },
      onClick,
      className: `relative flex flex-col items-start gap-2 rounded-xl border-2 p-3 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${isSelected ? "border-indigo-400 bg-indigo-50/60 shadow-sm dark:border-indigo-600 dark:bg-indigo-950/30" : "border-stone-200/80 bg-white hover:border-indigo-200 hover:bg-indigo-50/30 hover:shadow-sm dark:border-stone-700/50 dark:bg-stone-800/30 dark:hover:border-indigo-800 dark:hover:bg-indigo-950/20"}`,
      style: { minHeight: "76px" },
      children: [
        /* @__PURE__ */ jsx2("div", { className: `flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${colors.iconBg}`, children: /* @__PURE__ */ jsx2(FileText, { className: `h-3.5 w-3.5 ${colors.text}` }) }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx2("p", { className: "text-xs font-semibold leading-tight text-stone-800 dark:text-stone-200 line-clamp-1", children: template.title }),
          template.description && /* @__PURE__ */ jsx2("p", { className: "mt-0.5 text-[11px] leading-tight text-stone-500 dark:text-stone-400 line-clamp-2", children: template.description })
        ] }),
        isSelected && /* @__PURE__ */ jsx2(
          motion.div,
          {
            layoutId: "templateIndicator",
            className: "absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white shadow-sm shadow-indigo-300/50",
            transition: { type: "spring", stiffness: 500, damping: 30 },
            children: /* @__PURE__ */ jsx2(Check, { className: "h-3 w-3", strokeWidth: 3 })
          }
        )
      ]
    }
  );
}
var stepTitles = {
  1: { title: "Create with AI", subtitle: "Find a template or describe what you need" },
  2: { title: "Review Document", subtitle: "Preview the generated document blocks" }
};
function AIDocumentWizard({ onComplete, onBack, initialKind, allowedKinds }) {
  const authenticatedFetch = useAuthenticatedFetch();
  const handleError = useErrorHandler();
  const { getAccessToken, apiBaseUrl } = useEditorConfig();
  const defaultKind = initialKind || (allowedKinds?.length === 1 ? allowedKinds[0] : "document");
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [selectedKind] = useState(defaultKind);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [importMode, setImportMode] = useState("describe");
  const [importFile, setImportFile] = useState(null);
  const [importDragOver, setImportDragOver] = useState(false);
  const importFileInputRef = useRef(null);
  const [templateLibrary, setTemplateLibrary] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(null);
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const textareaRef = useRef(null);
  const selectedTemplateName = useMemo(() => {
    if (!selectedTemplateId) return null;
    for (const cat of templateLibrary) {
      const found = cat.templates.find((t) => t.id === selectedTemplateId);
      if (found) return found.title;
    }
    return null;
  }, [selectedTemplateId, templateLibrary]);
  useEffect(() => {
    let cancelled = false;
    const context = defaultKind === "form" ? "form" : "document";
    authenticatedFetch(
      `${API_ENDPOINTS.PROMPT_TEMPLATES}?context=${context}`
    ).then((data) => {
      if (!cancelled) setTemplateLibrary(data.categories);
    }).catch(() => {
    }).finally(() => {
      if (!cancelled) setTemplatesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const filteredCategories = selectedCategorySlug ? templateLibrary.filter((c) => c.slug === selectedCategorySlug) : templateLibrary;
  const allFilteredTemplates = filteredCategories.flatMap((cat) => {
    const templates = templateSearch.trim() ? cat.templates.filter(
      (t) => t.title.toLowerCase().includes(templateSearch.toLowerCase()) || (t.description || "").toLowerCase().includes(templateSearch.toLowerCase())
    ) : cat.templates;
    return templates.map((t) => ({ ...t, categorySlug: cat.slug, categoryColor: cat.color }));
  });
  const handleTemplateSelect = useCallback(
    (template) => {
      setPrompt(template.promptText);
      setSelectedTemplateId(template.id);
      setError(null);
      setTemplatePanelOpen(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
      authenticatedFetch(API_ENDPOINTS.PROMPT_TEMPLATE_USE(template.id), { method: "POST" }).catch(() => {
      });
    },
    [authenticatedFetch]
  );
  const handleImportFile = useCallback((file) => {
    setError(null);
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!IMPORT_SUPPORTED_EXT.includes(ext)) {
      setError("Unsupported file type. Allowed: PDF, DOCX, XLSX, JPEG, PNG, GIF, WebP.");
      return;
    }
    if (file.size > IMPORT_MAX_SIZE) {
      setError(`File too large (${(file.size / 1048576).toFixed(1)} MB). Max is 20 MB.`);
      return;
    }
    setImportFile(file);
  }, []);
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || prompt.length < 10) {
      setError("Please describe your document in more detail (at least 10 characters)");
      return;
    }
    if (prompt.length > 2e3) {
      setError("Prompt exceeds the maximum limit of 2000 characters.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const doc = await authenticatedFetch(API_ENDPOINTS.GENERATE_NDF, {
        method: "POST",
        body: JSON.stringify({
          prompt: prompt.trim(),
          kind: selectedKind,
          availableVariables: [
            "first_name",
            "last_name",
            "full_name",
            "email",
            "phone",
            "company",
            "company_address",
            "company_city",
            "company_state",
            "company_zip",
            "address",
            "city",
            "state",
            "zip",
            "country",
            "date",
            "today",
            "year",
            "month",
            "day",
            "document_id",
            "document_name",
            "expiration_date"
          ]
        })
      });
      setGeneratedDoc(doc);
      setDirection(1);
      setStep(2);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate document. Please try again.";
      setError(message);
      handleError(err instanceof Error ? err : new Error(message));
    } finally {
      setLoading(false);
    }
  }, [prompt, selectedKind, authenticatedFetch, handleError]);
  const handleImportGenerate = useCallback(async () => {
    if (!importFile) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append("file", importFile);
      const importKind = selectedKind === "auto" ? "document" : selectedKind;
      formData.append("kind", importKind);
      const response = await fetch(`${apiBaseUrl}${API_ENDPOINTS.IMPORT_DOCUMENT}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error?.message || `Import failed: ${response.status}`);
      }
      const json = await response.json();
      if (!json.success || !json.data) throw new Error(json.error?.message || "Import failed");
      setGeneratedDoc(json.data);
      setDirection(1);
      setStep(2);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to import document.";
      setError(msg);
      handleError(err instanceof Error ? err : new Error(msg));
    } finally {
      setLoading(false);
    }
  }, [importFile, selectedKind, getAccessToken, apiBaseUrl, handleError]);
  const handleBack = () => {
    if (step === 2) {
      setDirection(-1);
      setStep(1);
    } else {
      setImportFile(null);
      setImportMode("describe");
      onBack();
    }
  };
  const handleRegenerate = () => {
    setDirection(-1);
    setStep(1);
    setGeneratedDoc(null);
  };
  const handleUseDocument = () => {
    if (generatedDoc) {
      onComplete(generatedDoc);
    }
  };
  const canGenerate = importMode === "import" ? importFile !== null : prompt.trim().length >= 10;
  const currentStep = stepTitles[step];
  const blockCount = generatedDoc?.blocks.length || 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "flex h-full flex-col",
      style: { fontFamily: 'var(--font-display, "Plus Jakarta Sans", sans-serif)' },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "relative overflow-hidden border-b border-stone-200/80 bg-gradient-to-b from-white to-stone-50 px-6 py-5 dark:border-stone-700/50 dark:from-stone-800 dark:to-stone-900", children: [
          /* @__PURE__ */ jsx2(
            "div",
            {
              className: "pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full opacity-30 blur-3xl",
              style: {
                background: "linear-gradient(135deg, hsl(var(--fb-primary-500, 239 84% 67%)) 0%, hsl(var(--fb-accent-500, 25 95% 53%)) 100%)"
              }
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx2(
              motion.button,
              {
                variants: buttonVariants,
                initial: "initial",
                whileHover: "hover",
                whileTap: "tap",
                onClick: handleBack,
                className: "flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition-colors hover:bg-stone-50 hover:text-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-400 dark:hover:bg-stone-700 dark:hover:text-stone-200",
                "aria-label": "Go back",
                children: /* @__PURE__ */ jsx2(ArrowLeft, { className: "h-5 w-5" })
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx2("h2", { className: "text-lg font-semibold text-stone-900 dark:text-stone-100", children: currentStep.title }),
                /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm", children: [
                  /* @__PURE__ */ jsx2(Sparkles, { className: "h-2.5 w-2.5" }),
                  "AI"
                ] })
              ] }),
              /* @__PURE__ */ jsx2("p", { className: "mt-0.5 text-sm text-stone-500 dark:text-stone-400", children: step === 2 && generatedDoc ? `${blockCount} block${blockCount !== 1 ? "s" : ""} generated` : currentStep.subtitle })
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-4 flex items-center gap-2", children: [
            /* @__PURE__ */ jsx2(
              "div",
              {
                className: `h-1.5 flex-1 rounded-full transition-colors ${step >= 1 ? "bg-indigo-500" : "bg-stone-200 dark:bg-stone-700"}`
              }
            ),
            /* @__PURE__ */ jsx2(
              "div",
              {
                className: `h-1.5 flex-1 rounded-full transition-colors ${step >= 2 ? "bg-indigo-500" : "bg-stone-200 dark:bg-stone-700"}`
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx2("div", { className: "flex-1 overflow-y-auto bg-stone-50 dark:bg-stone-900", children: /* @__PURE__ */ jsx2(AnimatePresence, { mode: "wait", custom: direction, children: step === 1 ? /* @__PURE__ */ jsxs(
          motion.div,
          {
            custom: direction,
            variants: slideVariants,
            initial: "enter",
            animate: "center",
            exit: "exit",
            className: "p-6 space-y-5",
            children: [
              /* @__PURE__ */ jsx2("div", { className: "flex items-center gap-1 rounded-xl bg-stone-100 p-1 dark:bg-stone-800/60", children: [
                { mode: "describe", icon: /* @__PURE__ */ jsx2(Wand2, { className: "h-4 w-4" }), label: "Describe with AI" },
                { mode: "import", icon: /* @__PURE__ */ jsx2(FileUp, { className: "h-4 w-4" }), label: "Import Document" }
              ].map(({ mode, icon, label }) => /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: () => {
                    setImportMode(mode);
                    setError(null);
                  },
                  className: `flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${importMode === mode ? "bg-white text-stone-900 shadow-sm dark:bg-stone-700 dark:text-stone-100" : "text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"}`,
                  children: [
                    icon,
                    label
                  ]
                },
                mode
              )) }),
              importMode === "describe" && /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
                /* @__PURE__ */ jsxs("div", { className: "relative", children: [
                  /* @__PURE__ */ jsx2(
                    "textarea",
                    {
                      ref: textareaRef,
                      value: prompt,
                      onChange: (e) => {
                        setPrompt(e.target.value);
                        if (e.target.value !== prompt) setSelectedTemplateId(null);
                        if (error) setError(null);
                      },
                      placeholder: defaultKind === "form" ? 'Describe the form you need \u2014 e.g. "A patient intake form with medical history, allergies, and insurance fields" or "Employee onboarding form with tax withholding and direct deposit sections"' : 'Describe the legal document you need \u2014 e.g. "A mutual NDA between two software companies with a 3-year term" or "Employment offer letter with at-will clause and benefits overview"',
                      maxLength: 2e3,
                      rows: 5,
                      className: `block w-full resize-none rounded-xl border bg-white p-4 pb-8 text-sm text-stone-900 placeholder:text-stone-400 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:bg-stone-800 dark:text-stone-100 dark:placeholder:text-stone-500 ${error ? "border-red-300 focus:border-red-500 dark:border-red-800" : "border-stone-200 focus:border-indigo-500 dark:border-stone-700 dark:focus:border-indigo-500"}`,
                      "aria-label": "Document description",
                      "aria-invalid": !!error
                    }
                  ),
                  /* @__PURE__ */ jsx2("div", { className: "pointer-events-none absolute bottom-3 right-3", children: /* @__PURE__ */ jsxs("span", { className: `text-xs ${prompt.length > 2e3 ? "font-medium text-red-500 dark:text-red-400" : "text-stone-400"}`, children: [
                    prompt.length,
                    "/2000"
                  ] }) }),
                  prompt && /* @__PURE__ */ jsx2(
                    "button",
                    {
                      onClick: () => {
                        setPrompt("");
                        setSelectedTemplateId(null);
                        setError(null);
                      },
                      className: "absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 dark:hover:bg-stone-700 dark:hover:text-stone-200",
                      "aria-label": "Clear prompt",
                      children: /* @__PURE__ */ jsx2(X, { className: "h-3.5 w-3.5" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    onClick: () => setTemplatePanelOpen((o) => !o),
                    className: "flex w-full items-center justify-between rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm font-medium transition-all hover:border-stone-300 hover:bg-white dark:border-stone-700 dark:bg-stone-800/60 dark:hover:bg-stone-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                    children: [
                      /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2.5 min-w-0", children: [
                        /* @__PURE__ */ jsx2(BookOpen, { className: "h-4 w-4 shrink-0 text-indigo-500" }),
                        selectedTemplateName ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5 min-w-0", children: [
                          /* @__PURE__ */ jsx2("span", { className: "text-stone-400 dark:text-stone-500 shrink-0", children: "Template:" }),
                          /* @__PURE__ */ jsx2("span", { className: "truncate text-stone-800 dark:text-stone-200", children: selectedTemplateName })
                        ] }) : /* @__PURE__ */ jsxs("span", { className: "text-stone-600 dark:text-stone-400", children: [
                          "Browse ",
                          templateLibrary.reduce((n, c) => n + c.templates.length, 0) || (defaultKind === "form" ? 31 : 45),
                          " ",
                          defaultKind === "form" ? "form" : "legal document",
                          " templates"
                        ] })
                      ] }),
                      /* @__PURE__ */ jsx2(
                        ChevronDown,
                        {
                          className: `h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200 ${templatePanelOpen ? "rotate-180" : ""}`
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx2(AnimatePresence, { initial: false, children: templatePanelOpen && /* @__PURE__ */ jsx2(
                  motion.div,
                  {
                    initial: { height: 0, opacity: 0 },
                    animate: { height: "auto", opacity: 1 },
                    exit: { height: 0, opacity: 0 },
                    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
                    className: "overflow-hidden",
                    children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 rounded-xl border border-stone-200 bg-white p-3 dark:border-stone-700 dark:bg-stone-800/40", children: [
                      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
                        /* @__PURE__ */ jsx2(Search, { className: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" }),
                        /* @__PURE__ */ jsx2(
                          "input",
                          {
                            type: "text",
                            value: templateSearch,
                            onChange: (e) => setTemplateSearch(e.target.value),
                            placeholder: "Search templates...",
                            className: "w-full rounded-lg border border-stone-200 bg-stone-50 py-2 pl-9 pr-9 text-sm text-stone-900 placeholder:text-stone-400 transition-colors focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-100 dark:placeholder:text-stone-500 dark:focus:border-indigo-600"
                          }
                        ),
                        templateSearch && /* @__PURE__ */ jsx2(
                          "button",
                          {
                            onClick: () => setTemplateSearch(""),
                            className: "absolute right-3 top-1/2 -translate-y-1/2 rounded text-stone-400 hover:text-stone-600 dark:hover:text-stone-200",
                            "aria-label": "Clear search",
                            children: /* @__PURE__ */ jsx2(X, { className: "h-3.5 w-3.5" })
                          }
                        )
                      ] }),
                      !templatesLoading && templateLibrary.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex gap-1.5 overflow-x-auto pb-0.5", style: { scrollbarWidth: "none" }, children: [
                        /* @__PURE__ */ jsx2(
                          "button",
                          {
                            onClick: () => setSelectedCategorySlug(null),
                            className: `shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${!selectedCategorySlug ? "border-indigo-500 bg-indigo-600 text-white shadow-sm" : "border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:bg-stone-50 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-400 dark:hover:border-stone-500"}`,
                            children: "All"
                          }
                        ),
                        templateLibrary.map((cat) => /* @__PURE__ */ jsx2(
                          "button",
                          {
                            onClick: () => setSelectedCategorySlug(
                              cat.slug === selectedCategorySlug ? null : cat.slug
                            ),
                            className: `shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${selectedCategorySlug === cat.slug ? "border-indigo-500 bg-indigo-600 text-white shadow-sm" : "border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:bg-stone-50 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-400 dark:hover:border-stone-500"}`,
                            children: cat.name
                          },
                          cat.slug
                        ))
                      ] }),
                      /* @__PURE__ */ jsx2("div", { className: "max-h-52 overflow-y-auto", style: { scrollbarWidth: "thin" }, children: templatesLoading ? /* @__PURE__ */ jsx2(TemplateSkeletons, {}) : allFilteredTemplates.length > 0 ? /* @__PURE__ */ jsx2(
                        motion.div,
                        {
                          variants: containerVariants,
                          initial: "hidden",
                          animate: "visible",
                          className: "grid grid-cols-2 gap-2 sm:grid-cols-3",
                          children: allFilteredTemplates.map((template) => /* @__PURE__ */ jsx2(
                            TemplateCard,
                            {
                              template,
                              color: template.categoryColor,
                              isSelected: selectedTemplateId === template.id,
                              onClick: () => handleTemplateSelect(template)
                            },
                            template.id
                          ))
                        }
                      ) : /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-2 py-6 text-center", children: [
                        /* @__PURE__ */ jsx2(BookOpen, { className: "h-7 w-7 text-stone-300 dark:text-stone-600" }),
                        /* @__PURE__ */ jsxs("p", { className: "text-sm text-stone-500 dark:text-stone-400", children: [
                          "No templates match",
                          " ",
                          templateSearch && /* @__PURE__ */ jsxs("span", { children: [
                            '"',
                            /* @__PURE__ */ jsx2("span", { className: "font-medium", children: templateSearch }),
                            '"'
                          ] })
                        ] }),
                        templateSearch && /* @__PURE__ */ jsx2(
                          "button",
                          {
                            onClick: () => setTemplateSearch(""),
                            className: "text-xs text-indigo-600 hover:underline dark:text-indigo-400",
                            children: "Clear search"
                          }
                        )
                      ] }) })
                    ] })
                  },
                  "template-panel"
                ) })
              ] }),
              importMode === "import" && /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx2(
                  "input",
                  {
                    ref: importFileInputRef,
                    type: "file",
                    accept: ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp",
                    className: "sr-only",
                    onChange: (e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImportFile(file);
                      e.target.value = "";
                    }
                  }
                ),
                importFile ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 dark:border-indigo-900/50 dark:bg-indigo-950/30", children: [
                  /* @__PURE__ */ jsx2("div", { className: "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/50", children: /* @__PURE__ */ jsx2(FileType2, { className: "h-5 w-5 text-indigo-600 dark:text-indigo-400" }) }),
                  /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsx2("p", { className: "truncate text-sm font-medium text-stone-900 dark:text-stone-100", children: importFile.name }),
                    /* @__PURE__ */ jsxs("p", { className: "text-xs text-stone-500 dark:text-stone-400", children: [
                      (importFile.size / 1048576).toFixed(1),
                      " MB"
                    ] })
                  ] }),
                  /* @__PURE__ */ jsx2(
                    "button",
                    {
                      onClick: () => {
                        setImportFile(null);
                        setError(null);
                      },
                      className: "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 dark:hover:bg-stone-700 dark:hover:text-stone-200",
                      "aria-label": "Remove file",
                      children: /* @__PURE__ */ jsx2(X, { className: "h-4 w-4" })
                    }
                  )
                ] }) : /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    onClick: () => importFileInputRef.current?.click(),
                    onDragOver: (e) => {
                      e.preventDefault();
                      setImportDragOver(true);
                    },
                    onDragLeave: () => setImportDragOver(false),
                    onDrop: (e) => {
                      e.preventDefault();
                      setImportDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleImportFile(file);
                    },
                    className: `flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${importDragOver ? "border-indigo-400 bg-indigo-50/50 dark:border-indigo-600 dark:bg-indigo-950/20" : "border-stone-300 hover:border-indigo-300 hover:bg-stone-50/50 dark:border-stone-600 dark:hover:border-indigo-700 dark:hover:bg-stone-800/30"}`,
                    children: [
                      /* @__PURE__ */ jsx2("div", { className: `flex h-12 w-12 items-center justify-center rounded-xl transition-colors ${importDragOver ? "bg-indigo-100 dark:bg-indigo-900/50" : "bg-stone-100 dark:bg-stone-800"}`, children: /* @__PURE__ */ jsx2(PenLine, { className: `h-6 w-6 transition-colors ${importDragOver ? "text-indigo-600 dark:text-indigo-400" : "text-stone-400"}` }) }),
                      /* @__PURE__ */ jsxs("div", { children: [
                        /* @__PURE__ */ jsx2("p", { className: "font-medium text-stone-700 dark:text-stone-300", children: importDragOver ? "Drop to import" : "Upload a document" }),
                        /* @__PURE__ */ jsx2("p", { className: "mt-1 text-sm text-stone-500 dark:text-stone-400", children: "Drag & drop or click to browse" }),
                        /* @__PURE__ */ jsx2("p", { className: "mt-1 text-xs text-stone-400 dark:text-stone-500", children: "PDF, DOCX, XLSX, JPEG, PNG, GIF, WebP \u2014 up to 20 MB" })
                      ] })
                    ]
                  }
                )
              ] }),
              error && /* @__PURE__ */ jsxs(
                motion.div,
                {
                  initial: { opacity: 0, y: -8 },
                  animate: { opacity: 1, y: 0 },
                  className: "flex items-center gap-2 text-sm text-red-600 dark:text-red-400",
                  children: [
                    /* @__PURE__ */ jsx2(AlertCircle, { className: "h-4 w-4 shrink-0" }),
                    error
                  ]
                }
              )
            ]
          },
          "step1"
        ) : /* @__PURE__ */ jsx2(
          motion.div,
          {
            custom: direction,
            variants: slideVariants,
            initial: "enter",
            animate: "center",
            exit: "exit",
            className: "p-6",
            children: generatedDoc && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs("div", { className: "mb-6 rounded-xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50 to-violet-50 p-4 dark:border-indigo-900/50 dark:from-indigo-950/50 dark:to-violet-950/50", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3", children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsx2("h3", { className: "font-semibold text-stone-900 dark:text-stone-100", children: generatedDoc.meta.title }),
                    generatedDoc.meta.description && /* @__PURE__ */ jsx2("p", { className: "mt-1 text-sm text-stone-600 dark:text-stone-400", children: generatedDoc.meta.description })
                  ] }),
                  /* @__PURE__ */ jsxs(
                    "span",
                    {
                      className: `shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r ${kindBadgeConfig[generatedDoc.kind]?.gradient || "from-indigo-500 to-violet-500"} px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm`,
                      children: [
                        /* @__PURE__ */ jsx2(FileText, { className: "h-3 w-3" }),
                        kindBadgeConfig[generatedDoc.kind]?.label || generatedDoc.kind
                      ]
                    }
                  )
                ] }),
                generatedDoc.meta.variables && generatedDoc.meta.variables.length > 0 && /* @__PURE__ */ jsx2("div", { className: "mt-3 flex flex-wrap gap-1.5", children: generatedDoc.meta.variables.map((v) => /* @__PURE__ */ jsx2(
                  "span",
                  {
                    className: "inline-flex items-center rounded-md bg-white/60 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-200/50 dark:bg-indigo-900/30 dark:text-indigo-300 dark:ring-indigo-700/50",
                    children: `{{${v.name}}}`
                  },
                  v.name
                )) }),
                generatedDoc.settings && /* @__PURE__ */ jsxs("div", { className: "mt-3 flex flex-wrap gap-2 text-[11px] text-stone-500 dark:text-stone-400", children: [
                  generatedDoc.settings.submitButtonText && /* @__PURE__ */ jsxs("span", { className: "rounded bg-white/80 px-2 py-0.5 dark:bg-stone-800/50", children: [
                    'Submit: "',
                    generatedDoc.settings.submitButtonText,
                    '"'
                  ] }),
                  generatedDoc.settings.successMessage && /* @__PURE__ */ jsxs("span", { className: "rounded bg-white/80 px-2 py-0.5 dark:bg-stone-800/50", children: [
                    'Success: "',
                    generatedDoc.settings.successMessage,
                    '"'
                  ] })
                ] })
              ] }),
              /* @__PURE__ */ jsxs("div", { className: "mb-4 flex items-center justify-between", children: [
                /* @__PURE__ */ jsx2("p", { className: "text-sm font-medium text-stone-700 dark:text-stone-300", children: "Document blocks" }),
                /* @__PURE__ */ jsxs("span", { className: "text-xs text-stone-400 dark:text-stone-500", children: [
                  blockCount,
                  " block",
                  blockCount !== 1 ? "s" : ""
                ] })
              ] }),
              /* @__PURE__ */ jsx2(
                motion.div,
                {
                  variants: containerVariants,
                  initial: "hidden",
                  animate: "visible",
                  className: "space-y-2",
                  children: generatedDoc.blocks.map((block, index) => /* @__PURE__ */ jsx2(BlockPreview, { block, index }, block.id))
                }
              )
            ] })
          },
          "step2"
        ) }) }),
        /* @__PURE__ */ jsx2("div", { className: "border-t border-stone-200/80 bg-white/80 px-6 py-4 backdrop-blur-sm dark:border-stone-700/50 dark:bg-stone-800/80", children: step === 1 ? /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
          /* @__PURE__ */ jsx2("p", { className: "text-xs text-stone-400 dark:text-stone-500", children: importMode === "import" ? "AI will extract and convert the document structure to NDF" : "AI will generate a complete document based on your description" }),
          /* @__PURE__ */ jsx2(
            motion.button,
            {
              variants: buttonVariants,
              initial: "initial",
              whileHover: !loading && canGenerate ? "hover" : void 0,
              whileTap: !loading && canGenerate ? "tap" : void 0,
              onClick: importMode === "import" ? handleImportGenerate : handleGenerate,
              disabled: loading || !canGenerate,
              className: "inline-flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 font-medium text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none",
              style: { minWidth: "180px" },
              children: loading ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx2(Loader2, { className: "h-4 w-4 animate-spin" }),
                importMode === "import" ? "Importing..." : "Generating..."
              ] }) : importMode === "import" ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx2(FileUp, { className: "h-4 w-4" }),
                "Import Document"
              ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx2(Wand2, { className: "h-4 w-4" }),
                "Generate Document"
              ] })
            }
          )
        ] }) : /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: handleRegenerate,
              className: "inline-flex h-11 items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700",
              children: [
                /* @__PURE__ */ jsx2(RefreshCw, { className: "h-4 w-4" }),
                "Regenerate"
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            motion.button,
            {
              variants: buttonVariants,
              initial: "initial",
              whileHover: "hover",
              whileTap: "tap",
              onClick: handleUseDocument,
              className: "inline-flex h-11 items-center gap-2 rounded-xl bg-stone-900 px-6 font-medium text-white shadow-lg transition-all hover:bg-stone-800 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-100 dark:focus-visible:ring-white",
              style: { minWidth: "180px" },
              children: [
                /* @__PURE__ */ jsx2(Check, { className: "h-4 w-4" }),
                "Use This Document"
              ]
            }
          )
        ] }) })
      ]
    }
  );
}

// src/components/PDFUploadZone.tsx
import { useState as useState2, useRef as useRef2, useCallback as useCallback2 } from "react";
import { motion as motion2, AnimatePresence as AnimatePresence2 } from "framer-motion";
import { Button } from "@respectly/ui";
import {
  Upload as Upload2,
  AlertCircle as AlertCircle2,
  Loader2 as Loader22
} from "lucide-react";
import { cn } from "@respectly/ui";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var MAX_FILE_SIZE = 50 * 1024 * 1024;
function PDFUploadZone({
  onUploadSuccess,
  onUploadError,
  onLimitExceeded,
  onCancel,
  className,
  getToken,
  apiUrl,
  uploadOnly,
  onFileReady
}) {
  const [isDragOver, setIsDragOver] = useState2(false);
  const [isUploading, setIsUploading] = useState2(false);
  const [uploadProgress, setUploadProgress] = useState2(0);
  const [uploadedFile, setUploadedFile] = useState2(null);
  const [error, setError] = useState2(null);
  const fileInputRef = useRef2(null);
  const abortControllerRef = useRef2(null);
  const handleFile = useCallback2(
    async (file) => {
      setError(null);
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        setError("Only PDF files are allowed");
        onUploadError("Only PDF files are allowed");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError("File size exceeds 50MB limit");
        onUploadError("File size exceeds 50MB limit");
        return;
      }
      setUploadedFile(file);
      setIsUploading(true);
      setUploadProgress(0);
      abortControllerRef.current = new AbortController();
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", file.name.replace(/\.pdf$/i, ""));
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const progress = Math.round(e.loaded / e.total * 100);
            setUploadProgress(progress);
          }
        });
        const uploadPromise = new Promise((resolve, reject) => {
          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                if (response.success) {
                  resolve(response.data);
                } else {
                  reject(new Error(response.error?.message || "Upload failed"));
                }
              } catch {
                reject(new Error("Invalid response from server"));
              }
            } else {
              try {
                const errorData = JSON.parse(xhr.responseText);
                const err = new Error(errorData.error?.message || `Upload failed with status ${xhr.status}`);
                if (errorData.error?.code === "LIMIT_EXCEEDED" && errorData.error?.details) {
                  err.limitDetails = errorData.error.details;
                }
                reject(err);
              } catch {
                reject(new Error(`Upload failed with status ${xhr.status}`));
              }
            }
          });
          xhr.addEventListener("error", () => {
            reject(new Error("Network error during upload"));
          });
          xhr.addEventListener("abort", () => {
            reject(new Error("Upload cancelled"));
          });
        });
        const endpoint = uploadOnly ? `${apiUrl}/api/documents/upload-file` : `${apiUrl}/api/documents/upload`;
        xhr.open("POST", endpoint);
        const token = await getToken();
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        } else {
          throw new Error("Authentication required");
        }
        xhr.send(formData);
        abortControllerRef.current.signal.addEventListener("abort", () => {
          xhr.abort();
        });
        const result = await uploadPromise;
        setIsUploading(false);
        setUploadProgress(100);
        if (uploadOnly && onFileReady) {
          onFileReady({
            file,
            fileUrl: result.fileUrl,
            name: result.name,
            pageCount: result.pageCount,
            fileSize: result.fileSize,
            originalName: result.originalName
          });
        } else {
          onUploadSuccess(result);
        }
      } catch (err) {
        setIsUploading(false);
        const typedErr = err;
        if (typedErr.limitDetails && onLimitExceeded) {
          onLimitExceeded(typedErr.limitDetails);
        } else {
          const errorMessage = typedErr.message || "Upload failed";
          setError(errorMessage);
          onUploadError(errorMessage);
        }
      }
    },
    [onUploadSuccess, onUploadError, onFileReady, getToken, apiUrl, uploadOnly]
  );
  const handleDragOver = useCallback2((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback2((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback2(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      const firstFile = files[0];
      if (firstFile) {
        handleFile(firstFile);
      }
    },
    [handleFile]
  );
  const handleFileInputChange = useCallback2(
    (e) => {
      const files = e.target.files;
      const firstFile = files?.[0];
      if (firstFile) {
        handleFile(firstFile);
      }
    },
    [handleFile]
  );
  const handleCancel = useCallback2(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadedFile(null);
    setError(null);
    onCancel?.();
  }, [onCancel]);
  return /* @__PURE__ */ jsxs2("div", { className: cn("w-full", className), children: [
    /* @__PURE__ */ jsx3(
      "input",
      {
        ref: fileInputRef,
        type: "file",
        accept: "application/pdf,.pdf",
        className: "hidden",
        onChange: handleFileInputChange
      }
    ),
    /* @__PURE__ */ jsx3(
      "div",
      {
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        className: cn(
          "relative flex flex-col items-center justify-center",
          "min-h-[400px] md:min-h-[500px]",
          "border-2 border-dashed rounded-xl",
          "transition-all duration-200",
          isDragOver ? "border-primary-500 bg-primary-50" : "border-neutral-300 bg-neutral-50",
          isUploading && "pointer-events-none opacity-60"
        ),
        children: /* @__PURE__ */ jsxs2(AnimatePresence2, { mode: "wait", children: [
          !isUploading && !error && /* @__PURE__ */ jsxs2(
            motion2.div,
            {
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              className: "flex flex-col items-center justify-center px-6 py-12 text-center",
              children: [
                /* @__PURE__ */ jsx3("div", { className: "h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center mb-6", children: /* @__PURE__ */ jsx3(Upload2, { className: "h-10 w-10 text-primary-600" }) }),
                /* @__PURE__ */ jsx3("h3", { className: "text-xl font-semibold mb-2", children: "Upload PDF Template" }),
                /* @__PURE__ */ jsx3("p", { className: "text-sm text-muted-foreground mb-6 max-w-md", children: "Drag and drop your PDF file here, or tap to browse. We'll extract the pages so you can place signature fields and variables." }),
                /* @__PURE__ */ jsxs2(
                  Button,
                  {
                    size: "lg",
                    onClick: () => fileInputRef.current?.click(),
                    className: "mb-4",
                    children: [
                      /* @__PURE__ */ jsx3(Upload2, { className: "h-5 w-5 mr-2" }),
                      "Choose PDF File"
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-4 text-xs text-muted-foreground", children: [
                  /* @__PURE__ */ jsx3("span", { children: "PDF files only" }),
                  /* @__PURE__ */ jsx3("span", { children: "\u2022" }),
                  /* @__PURE__ */ jsx3("span", { children: "Max 50MB" })
                ] }),
                onCancel && /* @__PURE__ */ jsx3(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    onClick: onCancel,
                    className: "mt-6",
                    children: "Cancel"
                  }
                )
              ]
            },
            "upload-prompt"
          ),
          isUploading && /* @__PURE__ */ jsxs2(
            motion2.div,
            {
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              className: "flex flex-col items-center justify-center px-6 py-12 w-full max-w-md",
              children: [
                /* @__PURE__ */ jsx3("div", { className: "h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center mb-6", children: /* @__PURE__ */ jsx3(Loader22, { className: "h-8 w-8 text-primary-600 animate-spin" }) }),
                /* @__PURE__ */ jsx3("h3", { className: "text-lg font-semibold mb-2", children: "Uploading PDF..." }),
                /* @__PURE__ */ jsx3("p", { className: "text-sm text-muted-foreground mb-6", children: uploadedFile?.name }),
                /* @__PURE__ */ jsxs2("div", { className: "w-full space-y-2", children: [
                  /* @__PURE__ */ jsx3("div", { className: "w-full bg-neutral-200 rounded-full h-2 overflow-hidden", children: /* @__PURE__ */ jsx3(
                    "div",
                    {
                      className: "bg-primary-600 h-full transition-all duration-300",
                      style: { width: `${uploadProgress}%` }
                    }
                  ) }),
                  /* @__PURE__ */ jsxs2("p", { className: "text-sm text-center text-muted-foreground", children: [
                    uploadProgress,
                    "% complete"
                  ] })
                ] }),
                /* @__PURE__ */ jsx3(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    onClick: handleCancel,
                    className: "mt-6",
                    children: "Cancel Upload"
                  }
                )
              ]
            },
            "uploading"
          ),
          error && /* @__PURE__ */ jsxs2(
            motion2.div,
            {
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              className: "flex flex-col items-center justify-center px-6 py-12",
              children: [
                /* @__PURE__ */ jsx3("div", { className: "h-16 w-16 rounded-full bg-destructive-100 flex items-center justify-center mb-6", children: /* @__PURE__ */ jsx3(AlertCircle2, { className: "h-8 w-8 text-destructive-600" }) }),
                /* @__PURE__ */ jsx3("h3", { className: "text-lg font-semibold mb-2", children: "Upload Failed" }),
                /* @__PURE__ */ jsx3("p", { className: "text-sm text-destructive-600 mb-6", children: error }),
                /* @__PURE__ */ jsxs2("div", { className: "flex gap-3", children: [
                  /* @__PURE__ */ jsx3(
                    Button,
                    {
                      variant: "outline",
                      onClick: () => {
                        setError(null);
                        setUploadedFile(null);
                        fileInputRef.current?.click();
                      },
                      children: "Try Again"
                    }
                  ),
                  onCancel && /* @__PURE__ */ jsx3(Button, { variant: "ghost", onClick: handleCancel, children: "Cancel" })
                ] })
              ]
            },
            "error"
          )
        ] })
      }
    )
  ] });
}
export {
  AIDocumentWizard,
  EditorContextProvider,
  PDFUploadZone,
  useAuthenticatedFetch,
  useEditorConfig,
  useErrorHandler,
  useFeatureEnabled,
  useIsSuperAdmin
};
//# sourceMappingURL=index.js.map